"use client";
import React, { useState } from "react";
import { Clock, X } from "lucide-react";
import { useDashboard } from "../context/DashboardContext";
import { CDN_URL } from "../config/apiConfig";

export default function Announcements() {
  const { dashboardData, loading } = useDashboard();
  const [showModal, setShowModal] = useState(false);

  // Show loading UI
  if (loading) {
    return (
      <section className="h-full px-5 py-6 bg-white rounded-2xl border border-gray-100 shadow-md flex items-center justify-center">
        <p className="text-center text-sm text-gray-400">Loading announcements...</p>
      </section>
    );
  }

  // Convert API announcements → frontend format
  const announcements = dashboardData.announcements.map((a) => ({
    id: a.id,
    name : a.user_name,
    avatar: `${CDN_URL}/${a.user_image}`,
    message: a.title,
    timestamp: new Date(a.create_audit_time).toLocaleString("en-IN"),
    type: a.type, // fallback
  }));

  const displayedAnnouncements = announcements.slice(0, 4);

  const getTypeColor = (type) => {
    switch (type) {
      case "Request":
        return "bg-blue-100 text-blue-800";
      case "Approved":
        return "bg-green-100 text-green-800";
      case "Rejected":
        return "bg-red-100 text-red-800";
      case "Promotion":
        return "bg-purple-100 text-purple-800";
      case "New Hire":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "Request":
        return "Request";
      case "Approved":
        return "Approved";
      case "Rejected":
        return "Rejected";
      case "Promotion":
        return "Promotion";
      case "New Hire":
        return "New Hire";
      default:
        return "Update";
    }
  };

  return (
    <>
      {/* Main Announcement List */}
      <section className="h-full px-5 py-6 bg-white rounded-2xl border border-gray-100 shadow-md flex flex-col">
        <header className="flex justify-between items-center mb-5">
          <h2 className="text-xl font-bold text-gray-800">Announcements</h2>

          <button
            onClick={() => setShowModal(true)}
            className="text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors"
          >
            View All
          </button>
        </header>

        {displayedAnnouncements.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
            No announcements yet.
          </div>
        ) : (
          <div className="flex flex-col gap-3 overflow-y-auto">
            {displayedAnnouncements.map((announcement) => (
              <article
                key={announcement.id}
                className="group p-3 border border-gray-100 rounded-xl hover:bg-gray-50 hover:border-gray-200 transition-all duration-200"
              >
                <div className="flex gap-3 items-start">
                  <img
                    src={announcement.avatar}
                    alt=""
                    className="object-cover w-10 h-10 rounded-full ring-2 ring-gray-100 group-hover:ring-blue-200 transition-all duration-200 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 leading-relaxed">
                      <span className="font-semibold">{announcement.name}</span> {announcement.message}
                    </p>

                    <div className="flex items-center gap-1 text-xs text-gray-400 mt-1.5">
                      <Clock size={12} />
                      <time>{announcement.timestamp}</time>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-xl">
            <div className="flex justify-between items-center p-6 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">All Announcements</h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-3">
                {announcements.map((announcement) => (
                  <article
                    key={announcement.id}
                    className="p-4 border border-gray-100 rounded-xl hover:bg-gray-50 transition-colors duration-200"
                  >
                    <div className="flex gap-3 items-start">
                      <img
                        src={announcement.avatar}
                        alt=""
                        className="object-cover w-10 h-10 rounded-full ring-2 ring-gray-100"
                      />

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <p className="text-sm font-medium text-gray-800 leading-relaxed">
                            <span className="font-semibold">{announcement.name}</span> {announcement.message}
                          </p>

                          <span
                            className={`px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${getTypeColor(
                              announcement.type
                            )}`}
                          >
                            {getTypeLabel(announcement.type)}
                          </span>
                        </div>

                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Clock size={12} />
                          <time>{announcement.timestamp}</time>
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
